import { useEffect, useState } from 'react';
import { useAuth } from 'zite-auth-sdk';
import { getReviews, GetReviewsOutputType } from 'zite-endpoints-sdk';
import { Skeleton } from '@/components/ui/skeleton';
import { Star, Calendar, ClipboardList } from 'lucide-react';
import { format, parseISO } from 'date-fns';

type Review = GetReviewsOutputType['reviews'][0];

const fmtDate = (d?: string) => { try { return d ? format(parseISO(d), 'dd MMM yyyy') : '—'; } catch { return d || '—'; } };

function StarRating({ rating }: { rating?: number }) {
  if (!rating) return null;
  return (
    <div className="flex gap-0.5">
      {[1, 2, 3, 4, 5].map(i => (
        <Star
          key={i}
          className={`w-3.5 h-3.5 ${i <= rating ? 'text-primary fill-primary' : 'text-muted-foreground'}`}
        />
      ))}
    </div>
  );
}

export default function Reviews() {
  const { user } = useAuth();
  const [data, setData] = useState<GetReviewsOutputType | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user) return;
    getReviews({}).then(setData).finally(() => setLoading(false));
  }, [user]);

  const upcoming = data?.reviews.filter(r => r.status === 'Scheduled') || [];
  const past = data?.reviews.filter(r => r.status === 'Completed') || [];

  if (loading) {
    return (
      <div className="px-6 md:px-8 py-8 max-w-2xl mx-auto space-y-3">
        <Skeleton className="h-8 w-48" />
        {[...Array(3)].map((_, i) => <Skeleton key={i} className="h-24" />)}
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <div className="bg-card border-b border-border">
        <div className="px-6 md:px-8 py-6">
          <h1 className="text-xl font-bold text-foreground">My Reviews</h1>
          <p className="text-sm text-muted-foreground mt-1">Your performance reviews and objectives</p>
        </div>
      </div>

      <div className="px-6 md:px-8 py-6 max-w-2xl space-y-6">

        {data?.nextReviewDate && (
          <div className="flex items-center gap-3 bg-primary/5 border border-primary/20 rounded-lg px-4 py-3">
            <Calendar className="w-4 h-4 text-primary shrink-0" />
            <p className="text-sm text-foreground">
              Next scheduled review: <strong>{fmtDate(data.nextReviewDate)}</strong>
            </p>
          </div>
        )}

        {upcoming.length > 0 && (
          <div>
            <h2 className="text-sm font-semibold text-foreground mb-3 uppercase tracking-wider">Upcoming</h2>
            <div className="space-y-3">
              {upcoming.map(review => (
                <div key={review.id} className="border border-border rounded-lg bg-card p-4">
                  <div className="flex items-start justify-between gap-3 mb-2">
                    <p className="text-sm font-medium text-foreground">{review.reviewTitle}</p>
                    <span className="text-xs px-2 py-0.5 bg-muted text-muted-foreground rounded-sm whitespace-nowrap">
                      {fmtDate(review.reviewDate)}
                    </span>
                  </div>
                  {review.reviewerName && (
                    <p className="text-xs text-muted-foreground mb-1">Reviewer: {review.reviewerName}</p>
                  )}
                  {review.objectives && (
                    <div className="mt-2 p-3 bg-muted/40 rounded text-xs text-muted-foreground">
                      <strong className="text-foreground">Objectives: </strong>{review.objectives}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {past.length > 0 && (
          <div>
            <h2 className="text-sm font-semibold text-foreground mb-3 uppercase tracking-wider">Past Reviews</h2>
            <div className="space-y-3">
              {past.map(review => (
                <div key={review.id} className="border border-border rounded-lg bg-card p-4">
                  <div className="flex items-start justify-between gap-3 mb-2">
                    <div className="flex items-center gap-2">
                      <p className="text-sm font-medium text-foreground">{review.reviewTitle}</p>
                      <StarRating rating={review.rating} />
                    </div>
                    <span className="text-xs px-2 py-0.5 bg-primary/10 text-primary rounded-sm whitespace-nowrap">
                      Completed
                    </span>
                  </div>
                  <p className="text-xs text-muted-foreground mb-2">
                    {review.reviewDate && `${fmtDate(review.reviewDate)}`}
                    {review.reviewerName && ` · Reviewed by ${review.reviewerName}`}
                  </p>
                  {review.notes && (
                    <p className="text-xs text-muted-foreground bg-muted/40 rounded px-3 py-2 mb-2">{review.notes}</p>
                  )}
                  {review.actionPoints && (
                    <div className="flex items-start gap-2 text-xs">
                      <ClipboardList className="w-3.5 h-3.5 text-primary shrink-0 mt-0.5" />
                      <div>
                        <span className="text-foreground font-medium">Action points: </span>
                        <span className="text-muted-foreground">{review.actionPoints}</span>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {data?.reviews.length === 0 && (
          <div className="text-center py-16 text-muted-foreground">
            <Star className="w-10 h-10 mx-auto mb-3 opacity-30" />
            <p className="text-sm">No reviews yet. Your manager will schedule your first review.</p>
          </div>
        )}
      </div>
    </div>
  );
}
